import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  ContentState,
  convertFromHTML,
  convertFromRaw,
  convertToRaw,
  EditorState,
} from 'draft-js';
import WysiwygEditor from '@/components/WysiwygEditor';
import useSnackbar from '@/hooks/useSnackbar';
import useOrganizationId from '@/hooks/useOrganizationId';

const initialValues = {
  name: '',
  email: '',
  phone: '',
  subject: '',
};

interface FormValues {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message?: string;
}

const ContactForm = () => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const organizationId = useOrganizationId();
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [title, setTitle] = useState('Formulario de Contacto');
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );

  const handleClose = () => {
    const qs = new URLSearchParams(document.location.search);

    qs.delete('contact-form');
    qs.delete('contact-form-title');
    qs.delete('contact-form-subject');
    qs.delete('contact-form-message');

    const url = document.location.pathname + qs.toString();
    navigate(url, { replace: false });
    formik.setValues(initialValues);
  };

  const handleSubmit = async (value: FormValues) => {
    const URL = "https://contactform-sujjen5giq-uc.a.run.app";
    const payload = {
      phone: value.phone,
      email: value.email,
      subject: value.subject,
      name: value.name,
      message: 'Mensaje de ejemplo'
    }

    try {
      setLoading(true);
      await fetch(`${URL}/${organizationId}`, {
        method: 'POST',
        body: payload,
      })
      openSnackbar('Mensaje Enviado', 'success');
      handleClose();
    } catch (error) {
      console.error(error);
      openSnackbar('Error al enviar el correo', 'error');
    } finally {
      setLoading(false);
    }
  }

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (openDialog) {
      const qs = new URLSearchParams(document.location.search);
      const CFTitle = qs.get('contact-form-title');
      const subject = qs.get('contact-form-subject') ?? '';

      if (CFTitle) {
        setTitle(CFTitle);
      }

      formik.setValues({
        name: '',
        email: '',
        phone: '',
        subject,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, document.location]);

  useEffect(() => {
    if (openDialog) {
      const qs = new URLSearchParams(document.location.search);
      const stringifyHTML = decodeURIComponent(
        qs.get('contact-form-message') ?? ''
      );

      const { contentBlocks, entityMap } = convertFromHTML(stringifyHTML);
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      const raw = convertToRaw(contentState);
      setEditorState(EditorState.createWithContent(convertFromRaw(raw)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, document.location]);

  useEffect(() => {
    const qs = new URLSearchParams(document.location.search);
    setOpenDialog(qs.has('contact-form'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.location.search]);

  return (
    <Dialog open={openDialog} onClose={handleClose}>
      <Paper
        elevation={2}
        sx={{
          padding: 2,
          borderRadius: 2,
          minWidth: '600px',
        }}
      >
        <DialogTitle>
          <Stack
            spacing={2}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography>{title}</Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ paddingTop: 1 }}>
            <TextField
              name="name"
              label="Nombre"
              size="small"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <Stack spacing={2} direction="row">
              <TextField
                name="email"
                label="Correo"
                size="small"
                value={formik.values.email}
                onChange={formik.handleChange}
                fullWidth
              />
              <TextField
                name="phone"
                label="Teléfono"
                size="small"
                value={formik.values.phone}
                onChange={formik.handleChange}
                fullWidth
              />
            </Stack>
            <TextField
              name="subject"
              label="Asunto"
              size="small"
              value={formik.values.subject}
              onChange={formik.handleChange}
            />
            <WysiwygEditor value={editorState} onChange={setEditorState} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack spacing={2}>
            <Button onClick={formik.handleSubmit} disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar'}
            </Button>
          </Stack>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default ContactForm;
